import { useEffect } from 'react';
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';
import { T12Hero } from './components/T12Hero';
import { WhatIsT12 } from './components/WhatIsT12';
import { WhyT12Matters } from './components/WhyT12Matters';
import { T12vsOtherMetrics } from './components/T12vsOtherMetrics';
import { CalculatingT12 } from './components/CalculatingT12';
import { T12IncomeStatement } from './components/T12IncomeStatement';
import { T12ExpenseAnalysis } from './components/T12ExpenseAnalysis';
import { NOICalculation } from './components/NOICalculation';
import { T12InValuation } from './components/T12InValuation';
import { SeasonalityImpact } from './components/SeasonalityImpact';
import { T12RedFlags } from './components/T12RedFlags';
import { BuyerPerspective } from './components/BuyerPerspective';
import { SellerPerspective } from './components/SellerPerspective';
import { T12Documentation } from './components/T12Documentation';
import { CommonMistakes } from './components/CommonMistakes';
import { T12BestPractices } from './components/T12BestPractices';
import { CaseStudyExamples } from './components/CaseStudyExamples';
import { T12FAQ } from './components/T12FAQ';
import { T12FinalCTA } from './components/T12FinalCTA';

export default function Trailing12DeepDivePage() {
  useEffect(() => {
    // Set document title
    document.title = 'What is Trailing 12 (T12) in Real Estate? Complete Guide for Apartment Building Sellers';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master Trailing 12 (T12) financial statements for apartment building sales. Learn how T12 income, expenses, and NOI impact property valuation, buyer decisions, and sale price. Complete guide with examples and calculations.');
    }
    
    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'trailing 12, T12 real estate, trailing 12 months, apartment building financials, T12 income statement, NOI calculation, property valuation, T12 vs TTM, real estate financial analysis, apartment complex sale');
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://sellapartmentscash.com/how-it-works/trailing-12-deep-dive');
    
    // Set Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', 'What is Trailing 12 (T12) in Real Estate? Complete Deep Dive Guide');
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', 'Comprehensive guide to understanding Trailing 12 (T12) financial statements in apartment building sales. Learn calculations, analysis, and how T12 impacts your property\'s value.');
    
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', 'article');
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', 'https://sellapartmentscash.com/how-it-works/trailing-12-deep-dive');
    
    // Add structured data for Article
    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What is Trailing 12 (T12) in Real Estate? Complete Deep Dive Guide',
      description: 'Comprehensive guide to understanding Trailing 12 (T12) financial statements in apartment building sales, including calculations, analysis, and valuation impact.',
      author: {
        '@type': 'Organization',
        name: 'Sell Apartments Cash',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Sell Apartments Cash',
        logo: {
          '@type': 'ImageObject',
          url: 'https://sellapartmentscash.com/logo.png',
        },
      },
      datePublished: new Date().toISOString(),
      dateModified: new Date().toISOString(),
    });
    document.head.appendChild(articleScript);
    
    // Add structured data for FAQ
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What does Trailing 12 (T12) mean in real estate?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Trailing 12 (T12) refers to the financial performance of a property over the most recent 12-month period. It provides a comprehensive view of income, expenses, and net operating income (NOI) based on actual historical data rather than projections or pro forma estimates.',
          },
        },
        {
          '@type': 'Question',
          name: 'Why is T12 important when selling an apartment building?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'T12 is critical because it directly impacts property valuation and buyer confidence. Buyers use T12 to calculate NOI and cap rate, which determine the property\'s market value. Strong T12 financials can increase your sale price by 15-25%, while weak or incomplete T12 data can reduce offers or kill deals entirely.',
          },
        },
        {
          '@type': 'Question',
          name: 'What\'s the difference between T12 and TTM?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'T12 (Trailing 12) and TTM (Trailing Twelve Months) are essentially the same concept - both refer to the most recent 12-month period of financial performance. The terms are used interchangeably in commercial real estate, though T12 is more common in apartment building transactions.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do you calculate T12 NOI?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'T12 NOI is calculated by taking Total Income (rental income + other income) minus Total Operating Expenses (property taxes, insurance, utilities, maintenance, management fees, etc.) over the trailing 12-month period. The formula is: T12 NOI = T12 Gross Income - T12 Operating Expenses. Debt service and capital expenditures are NOT included in NOI calculations.',
          },
        },
        {
          '@type': 'Question',
          name: 'What documents do I need to provide for T12 analysis?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Essential T12 documents include: monthly income statements for the past 12 months, rent roll showing current tenant information and rental rates, operating expense records with receipts, property tax statements, insurance policies and costs, utility bills, maintenance and repair invoices, and bank statements showing actual deposits and payments.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I use projected income instead of actual T12?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Buyers and lenders require actual T12 data based on historical performance, not projections. While you can provide pro forma projections showing potential income after improvements, these must be clearly separated from actual T12 figures. Mixing projected and actual numbers is a major red flag that can kill your deal.',
          },
        },
        {
          '@type': 'Question',
          name: 'What are common T12 red flags that concern buyers?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Major red flags include: declining rental income trends, increasing vacancy rates, unusually low expenses suggesting deferred maintenance, missing or incomplete documentation, significant one-time income items inflating revenue, expenses that don\'t match industry standards, inconsistent month-to-month performance, and discrepancies between rent roll and actual collected rent.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does T12 affect my apartment building\'s value?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'T12 directly determines property value through the income capitalization approach. The formula is: Property Value = T12 NOI ÷ Cap Rate. For example, if your T12 NOI is $500,000 and the market cap rate is 6%, your property value is approximately $8.33 million. Every $10,000 increase in T12 NOI can add $166,667 to your property value at a 6% cap rate.',
          },
        },
        {
          '@type': 'Question',
          name: 'Should I wait to sell if my T12 is weak?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'It depends on your situation. If you can improve T12 performance within 3-6 months through rent increases, reducing vacancies, or cutting unnecessary expenses, waiting may increase your sale price significantly. However, if improvements will take 12+ months or require substantial capital investment, selling as-is to a cash buyer who understands value-add opportunities may be your best option.',
          },
        },
        {
          '@type': 'Question',
          name: 'How far back do buyers look beyond T12?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'While T12 is the primary focus, sophisticated buyers typically review 3-5 years of historical financial data to identify trends, verify consistency, and assess the property\'s performance through different market cycles. They look for patterns in income growth, expense management, occupancy rates, and capital expenditures to validate the T12 figures and project future performance.',
          },
        },
      ],
    });
    document.head.appendChild(faqScript);
    
    // Cleanup function
    return () => {
      if (articleScript.parentNode) {
        articleScript.parentNode.removeChild(articleScript);
      }
      if (faqScript.parentNode) {
        faqScript.parentNode.removeChild(faqScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main>
        <T12Hero />
        <WhatIsT12 />
        <WhyT12Matters />
        <T12vsOtherMetrics />
        <CalculatingT12 />
        <T12IncomeStatement />
        <T12ExpenseAnalysis />
        <NOICalculation />
        <T12InValuation />
        <SeasonalityImpact />
        <T12RedFlags />
        <BuyerPerspective />
        <SellerPerspective />
        <T12Documentation />
        <CommonMistakes />
        <T12BestPractices />
        <CaseStudyExamples />
        <T12FAQ />
        <T12FinalCTA />
      </main>

      <Footer />
    </div>
  );
}
